import { addDate, startsWeekOnSun } from 'src/helpers/date_helper';
import type { ChartStatisticRangePeriod } from 'src/types/Chart';

export interface RangeOptionInterface {
	period: ChartStatisticRangePeriod;
	timeRange: string;
	timeLabelFormat: string;
	enabled: boolean;
	label?: string;
}

export interface ChartControlsDataInterface {
	time: { from: Date; to: Date };
	format: string;
	rangeOption: RangeOptionInterface;
	currentPeriod: string;
}

export const LAYER_RANGE_OPTIONS: RangeOptionInterface[] = [
	{
		period: 'hour',
		timeRange: '1h',
		label: 'H',
		timeLabelFormat: 'hour',
		enabled: true,
	},
	{
		period: '24_hours',
		timeRange: '24h',
		label: '24H',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'day',
		timeRange: '1d',
		label: '1D',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'week',
		timeRange: '1w',
		label: '1W',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '7_days',
		timeRange: '7d',
		label: '7D',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '30_days',
		timeRange: '30d',
		label: '30D',
		timeLabelFormat: '30_days',
		enabled: true,
	},
	{
		period: 'month',
		timeRange: '1m',
		label: '1M',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: '6_months',
		timeRange: '6m',
		label: '6M',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: '12_months',
		timeRange: '12m',
		label: '12M',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'custom',
		timeRange: 'custom',
		label: 'custom',
		timeLabelFormat: 'custom',
		enabled: true,
	},
	{
		period: 'billing_date',
		timeRange: '1m',
		label: 'billing',
		timeLabelFormat: 'month',
		enabled: true,
	},
];

export const rangeDates = (
	date: Date,
	rangeOption: RangeOptionInterface,
	action: 'subtract' | 'add' | 'init' | 'click' = 'init',
	subtractOneSecond = false,
): { from: Date; to: Date } => {
	const init =
			action == 'init' && /^(24_hours|7_days|30_days|6_months|12_months)$/.test(rangeOption.period)
				? -1
				: 0,
		actions = { subtract: -1, add: 1, init, click: 0 },
		t = rangeOption.timeRange.split(/(\d+)/),
		timeValue = Number(t[0] + t[1]),
		timeRange = t[2],
		startWeekOnSun = /^w(eek)?/.test(timeRange) && startsWeekOnSun();
	let from = addDate(
		date,
		timeRange,
		timeValue * actions[action],
		!/^(billing_date)$/.test(rangeOption.period),
		startWeekOnSun,
	);
	if (init == -1) from = addDate(from, timeRange, 1, false, startWeekOnSun);
	let to = addDate(from, timeRange, timeValue, false, startWeekOnSun);
	if (subtractOneSecond) to = addDate(to, 's', -1);
	return { from, to };
};

export const buildChartRangeControls = (
	currentPeriod: ChartStatisticRangePeriod,
	date: Date,
	rolling = false,
): ChartControlsDataInterface => {
	const rangeOption =
		LAYER_RANGE_OPTIONS.find((option) => option.period === currentPeriod) ?? LAYER_RANGE_OPTIONS[1];
	const time = rolling
		? {
				from: addDate(
					date,
					rangeOption.timeRange.replace(/^\d+/, ''),
					-Number.parseInt(rangeOption.timeRange, 10),
				),
				to: date,
			}
		: rangeDates(date, rangeOption, 'init', true);

	return {
		time,
		format: 'Y-m-d H:i:s',
		rangeOption,
		currentPeriod,
	};
};
